import { useState } from 'react';
import { Stack, Group, Button, ActionIcon, Modal, Text } from '@mantine/core';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { useCreateOwner, useUpdateOwner, useDeleteOwner } from '@/hooks/useOwners';
import { OwnerForm } from './OwnerForm';
import { formatDate } from '@/utils/format';
import type { Owner } from '@/types/company';

interface OwnerListProps {
  companyId: string;
  owners: Owner[];
}

export function OwnerList({ companyId, owners }: OwnerListProps) {
  const [formOpened, setFormOpened] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Owner | null>(null);
  const createOwner = useCreateOwner();
  const updateOwner = useUpdateOwner();
  const deleteOwner = useDeleteOwner();

  const handleSave = (data: Omit<Owner, 'id' | 'companyId'>) => {
    if (editingOwner) {
      updateOwner.mutate(
        { companyId, ownerId: editingOwner.id, data },
        {
          onSuccess: () => {
            notifications.show({ message: 'Inhaber aktualisiert.', color: 'green' });
            setFormOpened(false);
            setEditingOwner(null);
          },
        },
      );
    } else {
      createOwner.mutate(
        { companyId, data },
        {
          onSuccess: () => {
            notifications.show({ message: 'Inhaber hinzugefügt.', color: 'green' });
            setFormOpened(false);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteOwner.mutate(
      { companyId, ownerId: deleteTarget.id },
      {
        onSuccess: () => {
          notifications.show({ message: 'Inhaber gelöscht.', color: 'green' });
          setDeleteTarget(null);
        },
      },
    );
  };

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button size="compact-sm" leftSection={<IconPlus size={14} />} onClick={() => { setEditingOwner(null); setFormOpened(true); }}>
          Inhaber hinzufügen
        </Button>
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        records={owners}
        columns={[
          { accessor: 'firstName', title: 'Vorname' },
          { accessor: 'lastName', title: 'Nachname' },
          {
            accessor: 'dateOfBirth',
            title: 'Geburtsdatum',
            render: (owner) => formatDate(owner.dateOfBirth),
          },
          {
            accessor: 'actions',
            title: '',
            width: 80,
            render: (owner) => (
              <Group gap="xs" wrap="nowrap">
                <ActionIcon variant="subtle" onClick={() => { setEditingOwner(owner); setFormOpened(true); }}>
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(owner)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
        noRecordsText="Keine Inhaber vorhanden"
      />

      <Modal
        opened={formOpened}
        onClose={() => { setFormOpened(false); setEditingOwner(null); }}
        title={editingOwner ? 'Inhaber bearbeiten' : 'Neuer Inhaber'}
      >
        <OwnerForm
          initial={editingOwner}
          onSave={handleSave}
          onCancel={() => { setFormOpened(false); setEditingOwner(null); }}
          loading={createOwner.isPending || updateOwner.isPending}
        />
      </Modal>

      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Inhaber löschen">
        <Stack>
          <Text>
            Möchten Sie <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong> wirklich löschen?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            <Button color="red" onClick={handleDelete} loading={deleteOwner.isPending}>Löschen</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
